import { useState, useRef } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Plus, X, Loader2 } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import { ActivePeriodsField, periodAttributes, periodRows, type ActivePeriod } from "@/components/ActivePeriodsField"

type VenueFormProps = {
  id: number | null
  slug?: string | null
  name: string
  address: string | null
  city: string
  state: string
  post_code: string | null
  region_id: number | null
  notes: string | null
  in_amh: boolean
  amh_url: string | null
  active_periods: ActivePeriod[]
}

type RegionOption = { id: number; name: string }

type PageProps = {
  venue: VenueFormProps
  regions: RegionOption[]
  return_to?: string | null
  errors?: Record<string, string>
}

const AU_STATES = [
  { value: "ACT", label: "ACT" },
  { value: "NSW", label: "NSW" },
  { value: "NT",  label: "NT"  },
  { value: "QLD", label: "QLD" },
  { value: "SA",  label: "SA"  },
  { value: "TAS", label: "TAS" },
  { value: "VIC", label: "VIC" },
  { value: "WA",  label: "WA"  },
]

const ic = "w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-primary"

function InlineRegionAdd({ onAdded }: { onAdded: (region: RegionOption) => void }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleAdd() {
    const trimmed = name.trim()
    if (!trimmed) return
    setSaving(true)
    setError(null)
    try {
      const res = await fetch("/admin/regions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
        },
        body: JSON.stringify({ region: { name: trimmed } }),
      })
      if (res.ok) {
        const data = await res.json()
        onAdded(data)
        setName("")
        setOpen(false)
      } else {
        const data = await res.json()
        setError(data.error ?? "Failed to add region")
      }
    } catch {
      setError("Failed to add region")
    } finally {
      setSaving(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50) }}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
      >
        <Plus className="h-3 w-3" /> Add new region
      </button>
    )
  }

  return (
    <div className="mt-2 flex items-start gap-2">
      <div className="flex-1">
        <input
          ref={inputRef}
          type="text"
          placeholder="New region name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); handleAdd() }
            if (e.key === "Escape") { setOpen(false); setName("") }
          }}
          className={ic}
        />
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
      <Button type="button" onClick={handleAdd} disabled={!name.trim() || saving}>
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
      </Button>
      <button type="button" onClick={() => { setOpen(false); setName("") }} className="text-ink-muted hover:text-ink-body pt-2">
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export default function VenueForm({ venue, regions: initialRegions, return_to }: { venue: VenueFormProps; regions: RegionOption[]; return_to?: string | null }) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = !venue.id
  const { active_periods, ...fields } = venue
  const [form, setForm] = useState(fields)
  const [periods, setPeriods] = useState(periodRows(active_periods))
  const [regions, setRegions] = useState<RegionOption[]>(initialRegions)

  function set(field: keyof typeof fields, value: unknown) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  function handleRegionAdded(region: RegionOption) {
    setRegions((r) => [...r, region].sort((a, b) => a.name.localeCompare(b.name)))
    set("region_id", region.id)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const params = return_to ? { return_to } : {}
    const payload = { venue: { ...form, active_periods_attributes: periodAttributes(periods) }, ...params }
    if (isNew) {
      router.post("/venues", payload)
    } else {
      router.patch(`/venues/${venue.slug}`, payload)
    }
  }

  const title = isNew ? "New venue" : `Edit ${venue.name}`
  const cancelHref = return_to ?? (venue.slug ? `/venues/${venue.slug}` : "/venues")

  return (
    <>
      <Head title={title}>
        <meta name="description" content={title} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={title} />
      </Head>
      <PublicShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <h1>{title}</h1>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display">Cancel</Link>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 max-w-lg space-y-5">
          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Name <span className="text-red-500">*</span></label>
            <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className={ic} required />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Address</label>
            <input type="text" value={form.address ?? ""} onChange={(e) => set("address", e.target.value)} className={ic} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-body mb-1">City <span className="text-red-500">*</span></label>
              <input type="text" value={form.city} onChange={(e) => set("city", e.target.value)} className={ic} required />
              {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-body mb-1">State <span className="text-red-500">*</span></label>
              <select value={form.state} onChange={(e) => set("state", e.target.value)} className={ic} required>
                <option value="">— Select —</option>
                {AU_STATES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {errors.state && <p className="mt-1 text-xs text-red-600">{errors.state}</p>}
            </div>
          </div>

          <ActivePeriodsField
            legend="Years open"
            legendClassName="block text-sm font-medium text-ink-body mb-1"
            hint="Add a period for each time it was open, e.g. closed and reopened. Leave the end blank if it's still open or not known."
            rows={periods} onChange={setPeriods} errors={errors} inputClassName={ic}
          />

          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Post code</label>
            <input type="text" value={form.post_code ?? ""} onChange={(e) => set("post_code", e.target.value)} className={`${ic} max-w-[160px]`} />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Region</label>
            <select value={form.region_id ?? ""} onChange={(e) => set("region_id", e.target.value ? Number(e.target.value) : null)} className={ic}>
              <option value="">— None —</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <InlineRegionAdd onAdded={handleRegionAdded} />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Notes</label>
            <textarea rows={4} value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} className={ic} />
          </div>

          <div className="flex items-center gap-2.5">
            <input
              type="checkbox"
              id="in_amh"
              checked={form.in_amh}
              onChange={(e) => set("in_amh", e.target.checked)}
              className="h-4 w-4 rounded border-hairline text-primary focus:ring-primary"
            />
            <label htmlFor="in_amh" className="text-sm text-ink-body">Featured on australianmusichistory.com</label>
          </div>

          <div>
            <label htmlFor="amh_url" className="block text-sm font-medium text-ink-body mb-1">AMH URL</label>
            <input
              id="amh_url"
              type="url"
              value={form.amh_url ?? ""}
              onChange={(e) => set("amh_url", e.target.value)}
              placeholder="https://australianmusichistory.com/…"
              className={ic}
            />
            <p className="mt-1 text-xs text-ink-muted">The venue's post on australianmusichistory.com, if it has one. Setting it ticks Featured.</p>
            {errors.amh_url && <p className="mt-1 text-xs text-red-600">{errors.amh_url}</p>}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create venue" : "Save changes"}</Button>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display">Cancel</Link>
          </div>
        </form>
      </PublicShell>
    </>
  )
}
